import React, { useState } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Badge,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiTarget,
  FiBarChart,
  FiCalendar,
  FiUsers,
  FiAward,
  FiClock,
  FiPlus,
  FiDownload,
  FiLock,
  FiStar,
  FiDollarSign,
} from "react-icons/fi";
import ProductivityReports from "./ProductivityReports";
import PerformanceReports from "./PerformanceReports";
import OvertimeReports from "./OvertimeReports"; // NEW: Import overtime reports
import AttendanceReport from "./AttendanceReport"; // NEW: Import attendance reports
import PayrollSheet from "../payrollsheet/PayrollSheet"; // NEW: Import payroll reports
import { usePlanRestriction } from "../../context/PlanRestrictionContext";
import { useUserContext } from "../../context/UserContext";
import BranchFilter from "../../components/BranchFilter";
import BranchAccessRestriction from "../../components/BranchAccessRestriction";
import {
  useGetOrganizationProductivity,
  useGetPerformanceInsights,
} from "../../services/productivityService";
import {
  useGetPerformanceInsights as useGetPerformanceInsightsData,
} from "../../services/performanceService";

const Reports = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedBranch, setSelectedBranch] = useState("all");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { canAccessFeature, showFeatureRestriction } = usePlanRestriction();
  const { user } = useUserContext();
  const isCompanyAdmin = user?.role === 'company_admin';

  // Fetch overview data
  const { organizationProductivityData } = useGetOrganizationProductivity();
  const { performanceInsightsData } = useGetPerformanceInsightsData();

  const handleExportReport = () => {
    toast({
      title: "Export initiated",
      description: "Report export has been started. You will receive a notification when ready.",
      status: "info",
      duration: 3000,
      isClosable: true,
      position: "top",
    });
  };

  const getTrendIcon = (trend) => {
    if (trend === "improving") return <FiTrendingUp color="green" />;
    if (trend === "declining") return <FiTrendingDown color="red" />;
    return <FiBarChart color="gray" />;
  };

  return (
    <Box w="100%" bg="gray.50" minH="100vh">
      {/* Header */}
      <VStack spacing={6} p={6} align="stretch">
        <HStack justify="space-between" align="center">
          <VStack align="start" spacing={2}>
            <Heading size="lg" color="gray.900" fontWeight="600">
              Reports & Analytics
            </Heading>
            <Text color="gray.600" fontSize="md">
              Comprehensive insights into productivity, performance, and overtime metrics
            </Text>
          </VStack>
          <HStack spacing={3}>
            <BranchFilter
              selectedBranch={selectedBranch}
              onBranchChange={setSelectedBranch}
              showLabel={false}
              maxW="250px"
            />
            <Button
              leftIcon={<FiDownload />}
              colorScheme="green"
              variant="outline"
              onClick={handleExportReport}
            >
              Export All Reports
            </Button>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={onOpen}
            >
              Generate Custom Report
            </Button>
          </HStack>
        </HStack>

        {/* Overview Stats */}
        <SimpleGrid columns={{ base: 1, sm: 2, md: 2, lg: 4 }} spacing={6}>
          <StatCard
            title="Overall Productivity"
            value={organizationProductivityData?.overall_productivity || 0}
            suffix="%"
            trend={organizationProductivityData?.trends?.current_month || "stable"}
            color="blue"
            icon={<FiTarget />}
          />
          <StatCard
            title="Total Employees"
            value={organizationProductivityData?.total_employees || 0}
            trend="stable"
            color="green"
            icon={<FiUsers />}
          />
          <StatCard
            title="High Performers"
            value={performanceInsightsData?.performance_summary?.high_performers || 0}
            trend="stable"
            color="purple"
            icon={<FiAward />}
          />
          <StatCard
            title="Average Attendance"
            value={organizationProductivityData?.overall_attendance || 0}
            suffix="%"
            trend="stable"
            color="orange"
            icon={<FiCalendar />}
          />
        </SimpleGrid>

        {/* Reports Tabs */}
        <Card>
          <CardBody p={0}>
            <Tabs variant="soft-rounded" colorScheme="blue" onChange={setActiveTab}>
              <TabList p={6} pb={0}>
                <Tab
                  _selected={{ bg: "blue.500", color: "white" }}
                  borderRadius="12px"
                  mr={4}
                  onClick={() => {}}
                >
                  <HStack spacing={2}>
                    <FiTarget />
                    <Text>Productivity Reports</Text>
                  </HStack>
                </Tab>
                <Tab
                  _selected={{ bg: "blue.500", color: "white" }}
                  borderRadius="12px"
                  mr={4}
                  onClick={() => {}}
                >
                  <HStack spacing={2}>
                    <FiBarChart />
                    <Text>Performance Reports</Text>
                  </HStack>
                </Tab>
                {/* NEW: Overtime Reports Tab */}
                <Tab
                  _selected={{ bg: "blue.500", color: "white" }}
                  borderRadius="12px"
                  mr={4}
                  onClick={() => {}}
                >
                  <HStack spacing={2}>
                    <FiClock />
                    <Text>Overtime Reports</Text>
                  </HStack>
                </Tab>
                {/* NEW: Attendance Reports Tab */}
                <Tab
                  _selected={{ bg: "blue.500", color: "white" }}
                  borderRadius="12px"
                  mr={4}
                  onClick={() => {}}
                >
                  <HStack spacing={2}>
                    <FiCalendar />
                    <Text>Attendance Reports</Text>
                  </HStack>
                </Tab>
                {/* NEW: Payroll Reports Tab */}
                <Tab
                  _selected={{ bg: "blue.500", color: "white" }}
                  borderRadius="12px"
                  onClick={() => {}}
                >
                  <HStack spacing={2}>
                    <FiDollarSign />
                    <Text>Payroll Reports</Text>
                  </HStack>
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel p={6} pt={4}>
                  {isCompanyAdmin ? (
                    <ProductivityReports selectedBranch={selectedBranch} />
                  ) : (
                    <BranchAccessRestriction featureName="Productivity Reports" />
                  )}
                </TabPanel>
                <TabPanel p={6} pt={4}>
                  {isCompanyAdmin ? (
                    <PerformanceReports selectedBranch={selectedBranch} />
                  ) : (
                    <BranchAccessRestriction featureName="Performance Reports" />
                  )}
                </TabPanel>
                {/* NEW: Overtime Reports Tab Panel */}
                <TabPanel p={6} pt={4}>
                  {isCompanyAdmin ? (
                    <OvertimeReports selectedBranch={selectedBranch} />
                  ) : (
                    <BranchAccessRestriction featureName="Overtime Reports" />
                  )}
                </TabPanel>
                {/* NEW: Attendance Reports Tab Panel */}
                <TabPanel p={6} pt={4}>
                  <AttendanceReport />
                </TabPanel>
                {/* NEW: Payroll Reports Tab Panel */}
                <TabPanel p={6} pt={4}>
                  {isCompanyAdmin ? (
                    <PayrollSheet />
                  ) : (
                    <BranchAccessRestriction featureName="Payroll Reports" />
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      </VStack>

      {/* Custom Report Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Generate Custom Report</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={4}>
              <Text color="gray.600">
                Custom report generation feature is coming soon. This will allow you to create personalized reports with specific metrics and date ranges.
              </Text>
              <Button
                colorScheme="blue"
                onClick={onClose}
                w="100%"
              >
                Got it
              </Button>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// Stat Card Component
const StatCard = ({ title, value, suffix = "", trend, color, icon }) => {
  const colorScheme = {
    blue: { bg: "blue.50", color: "blue.600", border: "blue.200" },
    green: { bg: "green.50", color: "green.600", border: "green.200" },
    purple: { bg: "purple.50", color: "purple.600", border: "purple.200" },
    orange: { bg: "orange.50", color: "orange.600", border: "orange.200" },
  };

  const colors = colorScheme[color] || colorScheme.blue;

  const getTrendIcon = (trend) => {
    if (trend === "improving") return <FiTrendingUp color="green" />;
    if (trend === "declining") return <FiTrendingDown color="red" />;
    return <FiBarChart color="gray" />;
  };

  return (
    <Card>
      <CardBody>
        <HStack spacing={4} align="center">
          <Box
            p={3}
            borderRadius="12px"
            bg={colors.bg}
            color={colors.color}
            border="1px solid"
            borderColor={colors.border}
          >
            {icon}
          </Box>
          <VStack spacing={1} align="start" flex={1}>
            <Text fontSize="sm" color="gray.600" fontWeight="500">
              {title}
            </Text>
            <Text fontSize="2xl" color="gray.900" fontWeight="700">
              {value}{suffix}
            </Text>
            <HStack spacing={1}>
              {getTrendIcon(trend)}
              <Text fontSize="xs" color="gray.500" textTransform="capitalize">
                {trend}
              </Text>
            </HStack>
          </VStack>
        </HStack>
      </CardBody>
    </Card>
  );
};

export default Reports;
